import { type ReactNode } from "react";
import { Link, usePermissions, useRecordContext, type RaRecord } from "react-admin";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { formatListDate } from "@/utils/date-format";
import {
  eventNeedsAnySadarat,
  formatRazaEventSadaratListLabel,
} from "@/utils/format-raza-event-sadarat-display";
import { hasPermission } from "@/utils/permission-utils";
import {
  openRazaEventOhbatKhidmatDialog,
  openRazaEventOhbatSadaratDialog,
  openRazaEventOhbatZakereenDialog,
} from "./raza-events-show-dialog-openers";
import { shouldShowRazaEventSendWhatsApp } from "./raza-event-send-whatsapp";
import {
  hostConfirmationChipColor,
  hostConfirmationStatusLabel,
  hostConfirmationStatusTimestamp,
  type LatestHostConfirmation,
} from "./raza-event-host-confirmation-status";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { isOhbatRazaEvent, razaEventKindLabel } from "@/utils/raza-event-display";
import {
  formatRazaEventVenueForDisplay,
  resolveRazaEventVenueFromRecord,
} from "@/utils/raza-event-venue-display";

const dash = (v: unknown) => (v != null && String(v).trim() !== "" ? String(v) : "—");

function trimStr(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

function razaEventPurposeNameOnly(record: RaRecord): string {
  const purposeName = trimStr(record.purposeName);
  if (purposeName) return purposeName;
  const razaRequest = record.razaRequest as { purpose?: string } | undefined;
  return trimStr(razaRequest?.purpose);
}

function razaEventHallNames(record: RaRecord): string {
  const hallsRaw = record.hallBookings as
    | Array<{ hall?: { name?: string | null } | null }>
    | undefined;
  const hallNames = [
    ...new Set(
      (hallsRaw ?? []).map((hb) => trimStr(hb.hall?.name)).filter((name) => name.length > 0)
    ),
  ];
  return hallNames.length > 0 ? hallNames.join(", ") : "";
}

function SummaryTable({
  title,
  titleAddon,
  children,
}: {
  title: string;
  titleAddon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography variant="h6" component="h3" sx={{ m: 0 }}>
          {title}
        </Typography>
        {titleAddon}
      </Box>
      <Table size="small" aria-label={title} sx={{ borderTop: 1, borderTopColor: "divider" }}>
        <TableBody>{children}</TableBody>
      </Table>
    </>
  );
}

function SummaryRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | null;
}) {
  return (
    <TableRow>
      <TableCell sx={{ py: 1, verticalAlign: "top", borderColor: "divider" }}>
        <Typography
          component="div"
          sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}
        >
          <span>{label}:</span>
          <Box sx={{ textAlign: "right", wordBreak: "break-word" }}>
            <strong>{value}</strong>
            {hint ? (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mt: 0.25 }}
              >
                {hint}
              </Typography>
            ) : null}
          </Box>
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * Compact two-column summary (hall booking–style) for raza event show.
 */
export function RazaEventsDetailsTab() {
  const record = useRecordContext() as RaRecord | undefined;
  const { permissions } = usePermissions();
  const canEditMajlis = hasPermission(permissions, "razaEvents.edit");

  if (!record) {
    return null;
  }

  const isOhbat = isOhbatRazaEvent(record);
  const timeLabel = formatRazaEventStartTimeLabel(record.startTime as string | undefined);
  const dateLabel = record.date ? formatListDate(String(record.date)) : "—";
  const showSadarat = eventNeedsAnySadarat({
    needSadaratMale: record.needSadaratMale as boolean | null | undefined,
    needSadaratFemale: record.needSadaratFemale as boolean | null | undefined,
    needSadarat: record.needSadarat as boolean | null | undefined,
  });
  const hallNames = razaEventHallNames(record);
  const venueDisplay = formatRazaEventVenueForDisplay(
    resolveRazaEventVenueFromRecord({
      address: record.address as string | null | undefined,
      hallBookings: record.hallBookings as
        | Array<{ hall?: { venue?: string | null } | null }>
        | undefined,
    })
  );
  // Status chip uses same visibility as the former manual Send WhatsApp control.
  // Manual resend remains available via POST /raza-events/:id/send-confirmation.
  const showHostConfirmationStatus = shouldShowRazaEventSendWhatsApp({
    needSadaratMale: record.needSadaratMale,
    needSadaratFemale: record.needSadaratFemale,
    needSadarat: record.needSadarat,
    canEdit: canEditMajlis,
  });
  const latestHostConfirmation = record.latestHostConfirmation as
    | LatestHostConfirmation
    | null
    | undefined;

  const khidmatguzar = record.khidmatguzar as { ITS_ID?: string } | undefined;
  const khidmatIts = khidmatguzar?.ITS_ID ?? (record.khidmatguzarItsNo as string | undefined);
  const zakereenRow = record.zakereen as { ITS_ID?: string } | undefined;
  const zakereenIts = zakereenRow?.ITS_ID ?? (record.zakereenItsNo as string | undefined);

  const sadaratEditIcon =
    isOhbat && canEditMajlis ? (
      <Tooltip title="Change sadarat assignment">
        <IconButton
          size="small"
          aria-label="Change sadarat assignment"
          onClick={() => openRazaEventOhbatSadaratDialog()}
          edge="end"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  const khidmatEditIcon =
    isOhbat && canEditMajlis ? (
      <Tooltip title="Change khidmatguzar">
        <IconButton
          size="small"
          aria-label="Change khidmatguzar"
          onClick={() => openRazaEventOhbatKhidmatDialog()}
          edge="end"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  const zakereenEditIcon =
    isOhbat && canEditMajlis ? (
      <Tooltip title="Change zakereen">
        <IconButton
          size="small"
          aria-label="Change zakereen"
          onClick={() => openRazaEventOhbatZakereenDialog()}
          edge="end"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  type SadaratContact = { name?: string; itsNo?: string; mobile?: string };
  const sadaratMale = record.sadaratMale as SadaratContact | undefined;
  const sadaratFemale = record.sadaratFemale as SadaratContact | undefined;
  const sadaratLegacy = record.sadarat as SadaratContact | undefined;
  const sadaratSummaryLabel = formatRazaEventSadaratListLabel({
    needSadaratMale: record.needSadaratMale as boolean | null | undefined,
    needSadaratFemale: record.needSadaratFemale as boolean | null | undefined,
    sadaratMale,
    sadaratFemale,
    needSadarat: record.needSadarat as boolean | null | undefined,
    sadarat: sadaratLegacy,
  });
  const khidmat = record.khidmatguzar as { Full_Name?: string; Mobile?: string } | undefined;
  const zakereen = record.zakereen as { Full_Name?: string; Mobile?: string } | undefined;

  const hostConfirmationAt = hostConfirmationStatusTimestamp(latestHostConfirmation);
  const hostConfirmationStatusUi =
    showHostConfirmationStatus && latestHostConfirmation?.status ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Host confirmation
        </Typography>
        <Chip
          size="small"
          label={hostConfirmationStatusLabel(latestHostConfirmation.status)}
          color={hostConfirmationChipColor(latestHostConfirmation.status)}
        />
        {hostConfirmationAt ? (
          <Typography variant="caption" color="text.secondary">
            {hostConfirmationAt}
          </Typography>
        ) : null}
        {String(latestHostConfirmation.status).toUpperCase() === "FAILED" &&
        trimStr(latestHostConfirmation.errorMessage) ? (
          <Typography variant="caption" color="error">
            {trimStr(latestHostConfirmation.errorMessage)}
          </Typography>
        ) : null}
      </Box>
    ) : null;

  if (!isOhbat) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: 2 }}>
        {hostConfirmationStatusUi}
        <Grid container spacing={3}>
          <Grid
            sx={(theme) => ({
              borderRight: { md: `1px solid ${theme.palette.divider}` },
              pr: { md: 2 },
            })}
            size={{ xs: 12, md: 6 }}
          >
            <SummaryTable title="Event & schedule">
              <SummaryRow label="Date" value={dateLabel} />
              <SummaryRow label="Time" value={timeLabel} />
              <SummaryRow label="Kind" value={razaEventKindLabel(record)} />
              <SummaryRow label="Title" value={dash(record.title)} />
              <SummaryRow label="Purpose" value={dash(razaEventPurposeNameOnly(record))} />
            </SummaryTable>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SummaryTable title="Host & venue">
              <SummaryRow label="Host ITS" value={dash(record.hostItsNo)} />
              <SummaryRow label="Host name" value={dash(record.hostName)} />
              <SummaryRow label="Sector (from host ITS)" value={dash(record.hostSector)} />
              <SummaryRow label="Sub-sector (from host ITS)" value={dash(record.hostSubSector)} />
              <SummaryRow label="Halls" value={dash(hallNames)} />
              <SummaryRow
                label="Venue address"
                value={venueDisplay.value}
                hint={venueDisplay.hint}
              />
              <SummaryRow label="Contact mobile" value={dash(record.mobileNo)} />
            </SummaryTable>
          </Grid>
          {showSadarat ? (
            <Grid
              sx={(theme) => ({
                borderRight: { md: `1px solid ${theme.palette.divider}` },
                pr: { md: 2 },
              })}
              size={{ xs: 12, md: 6 }}
            >
              <SummaryTable title="Sadarat (linked)">
                <SummaryRow label="Assigned" value={sadaratSummaryLabel} />
                {record.needSadaratMale ? (
                  <>
                    <SummaryRow label="Male name" value={dash(sadaratMale?.name)} />
                    <SummaryRow label="Male ITS" value={dash(sadaratMale?.itsNo)} />
                    <SummaryRow label="Male mobile" value={dash(sadaratMale?.mobile)} />
                  </>
                ) : null}
                {record.needSadaratFemale ? (
                  <>
                    <SummaryRow label="Female name" value={dash(sadaratFemale?.name)} />
                    <SummaryRow label="Female ITS" value={dash(sadaratFemale?.itsNo)} />
                    <SummaryRow label="Female mobile" value={dash(sadaratFemale?.mobile)} />
                  </>
                ) : null}
                {!record.needSadaratMale && !record.needSadaratFemale && sadaratLegacy ? (
                  <>
                    <SummaryRow label="Name" value={dash(sadaratLegacy?.name)} />
                    <SummaryRow label="ITS" value={dash(sadaratLegacy?.itsNo)} />
                    <SummaryRow label="Mobile" value={dash(sadaratLegacy?.mobile)} />
                  </>
                ) : null}
              </SummaryTable>
            </Grid>
          ) : null}
          {record.razaRequestId ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <SummaryTable title="Raza request">
                <TableRow>
                  <TableCell sx={{ py: 1, verticalAlign: "top", borderColor: "divider" }}>
                    <Typography
                      component="div"
                      sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}
                    >
                      <span>Request:</span>
                      <Link to={`/razaRequests/${String(record.razaRequestId)}/show`}>
                        View request
                      </Link>
                    </Typography>
                  </TableCell>
                </TableRow>
              </SummaryTable>
            </Grid>
          ) : null}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: 2 }}>
      {hostConfirmationStatusUi}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryTable title="Host & venue">
            <SummaryRow label="Host ITS" value={dash(record.hostItsNo)} />
            <SummaryRow label="Host name" value={dash(record.hostName)} />
            <SummaryRow label="Sector (from host ITS)" value={dash(record.hostSector)} />
            <SummaryRow label="Sub-sector (from host ITS)" value={dash(record.hostSubSector)} />
            <SummaryRow label="Venue address" value={venueDisplay.value} hint={venueDisplay.hint} />
            <SummaryRow label="Contact mobile" value={dash(record.mobileNo)} />
            <TableRow>
              <TableCell sx={{ py: 1, verticalAlign: "top", borderColor: "divider" }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Notes
                </Typography>
                <Typography
                  component="div"
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: { xs: 100, sm: 140 },
                    overflow: "auto",
                    pr: 0.5,
                  }}
                >
                  {dash(record.notes)}
                </Typography>
              </TableCell>
            </TableRow>
          </SummaryTable>
        </Grid>
        <Grid
          sx={(theme) => ({
            borderRight: { md: `1px solid ${theme.palette.divider}` },
            pr: { md: 2 },
          })}
          size={{ xs: 12, md: 6 }}
        >
          <SummaryTable title="Majlis & schedule">
            <SummaryRow label="Date" value={dateLabel} />
            <SummaryRow label="Time" value={timeLabel} />
            <SummaryRow label="Type" value={dash(record.ohbatType)} />
          </SummaryTable>
        </Grid>
        <Grid
          sx={(theme) => ({
            borderRight: { md: `1px solid ${theme.palette.divider}` },
            pr: { md: 2 },
          })}
          size={{ xs: 12, md: 6 }}
        >
          <SummaryTable title="Sadarat (linked)" titleAddon={sadaratEditIcon}>
            <SummaryRow label="Assigned" value={sadaratSummaryLabel} />
            {record.needSadaratMale ? (
              <>
                <SummaryRow label="Male name" value={dash(sadaratMale?.name)} />
                <SummaryRow label="Male ITS" value={dash(sadaratMale?.itsNo)} />
                <SummaryRow label="Male mobile" value={dash(sadaratMale?.mobile)} />
              </>
            ) : null}
            {record.needSadaratFemale ? (
              <>
                <SummaryRow label="Female name" value={dash(sadaratFemale?.name)} />
                <SummaryRow label="Female ITS" value={dash(sadaratFemale?.itsNo)} />
                <SummaryRow label="Female mobile" value={dash(sadaratFemale?.mobile)} />
              </>
            ) : null}
            {!record.needSadaratMale && !record.needSadaratFemale && sadaratLegacy ? (
              <>
                <SummaryRow label="Name" value={dash(sadaratLegacy?.name)} />
                <SummaryRow label="ITS" value={dash(sadaratLegacy?.itsNo)} />
                <SummaryRow label="Mobile" value={dash(sadaratLegacy?.mobile)} />
              </>
            ) : null}
          </SummaryTable>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryTable title="Khidmatguzar (itsdata)" titleAddon={khidmatEditIcon}>
            <SummaryRow label="ITS" value={dash(khidmatIts)} />
            <SummaryRow label="Name" value={dash(khidmat?.Full_Name)} />
            <SummaryRow label="Mobile" value={dash(khidmat?.Mobile)} />
          </SummaryTable>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SummaryTable title="Zakereen (itsdata)" titleAddon={zakereenEditIcon}>
            <SummaryRow label="ITS" value={dash(zakereenIts)} />
            <SummaryRow label="Name" value={dash(zakereen?.Full_Name)} />
            <SummaryRow label="Mobile" value={dash(zakereen?.Mobile)} />
          </SummaryTable>
        </Grid>
      </Grid>
    </Box>
  );
}
